import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Users, FileText, Flag, ArrowLeft, Search, Settings, Trash2, Archive, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable column header component
function SortHeader({ label, field, sort, onSort }) {
  const isActive = sort.field === field;
  return (
    <TableHead
      className="text-[#54595d] cursor-pointer select-none hover:bg-[#eaecf0] transition-colors"
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sort.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-[#36c]" /> : <ChevronDown className="h-3 w-3 text-[#36c]" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-[#a2a9b1]" />
        )}
      </div>
    </TableHead>
  );
}

function applySortAndFilter(items, sort, getValue) {
  const sorted = [...items].sort((a, b) => {
    const av = getValue(a, sort.field) ?? '';
    const bv = getValue(b, sort.field) ?? '';
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, getAuthHeader, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  // Sort states
  const [userSort, setUserSort] = useState({ field: 'created_at', dir: 'desc' });
  const [pageSort, setPageSort] = useState({ field: 'updated_at', dir: 'desc' });
  const [reportSort, setReportSort] = useState({ field: 'created_at', dir: 'desc' });

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, username }

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, pagesRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/pages?limit=100`),
        axios.get(`${API}/reports`, { headers: getAuthHeader() })
      ]);
      setUsers(usersRes.data);
      setPages(pagesRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin()) { navigate('/'); return; }
    fetchData();
  }, [user, authLoading, navigate, isAdmin, fetchData]);

  // Sort toggle
  const toggleSort = (current, field, setter) => {
    if (current.field === field) {
      setter({ field, dir: current.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setter({ field, dir: 'asc' });
    }
  };

  // Sorted/filtered users
  const filteredUsers = applySortAndFilter(
    users.filter(u => {
      const matchesSearch = u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
                           u.email.toLowerCase().includes(userSearch.toLowerCase());
      const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
      return matchesSearch && matchesRole;
    }),
    userSort,
    (u, f) => f === 'created_at' ? u.created_at : u[f]
  );

  // Sorted pages
  const sortedPages = applySortAndFilter(pages, pageSort, (p, f) => {
    if (f === 'updated_at') return p.updated_at;
    if (f === 'view_count') return Number(p.view_count ?? 0);
    if (f === 'title') return p.title?.toLowerCase();
    if (f === 'status') return p.is_sponsored ? 'sponsored' : p.is_locked ? 'locked' : '';
    return p[f];
  });

  // Sorted reports (filtered by archived toggle)
  const sortedReports = applySortAndFilter(
    reports.filter(r => showArchived ? r.status === 'archived' : r.status !== 'archived'),
    reportSort,
    (r, f) => f === 'created_at' ? r.created_at : r[f]
  );

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`, { headers: getAuthHeader() });
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      toast.success(`User "${deleteTarget.username}" deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteTarget(null);
    }
  };

  // Archive report
  const handleArchiveReport = async (reportId) => {
    try {
      await axios.put(`${API}/reports/${reportId}/archive`, {}, { headers: getAuthHeader() });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'archived' } : r));
      toast.success('Report archived');
    } catch (error) {
      toast.error('Failed to archive report');
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#eaecf0] rounded w-1/4" />
            <div className="h-64 bg-[#eaecf0] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/" className="text-sm text-[#36c] hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
            <h1 className="font-serif text-3xl font-bold text-[#202122] flex items-center gap-3">
              <Shield className="h-8 w-8 text-[#36c]" />
              Admin Dashboard
            </h1>
          </div>
          {isSuperAdmin() && (
            <Link to="/admin/config">
              <Button className="bg-[#36c] text-white hover:bg-[#2a4b8d]" data-testid="super-admin-config-btn">
                <Settings className="h-4 w-4 mr-2" />
                Super Admin Config
              </Button>
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-6 border border-[#a2a9b1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#eaf3ff] flex items-center justify-center">
                <Users className="h-5 w-5 text-[#36c]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#202122]">{users.length}</p>
                <p className="text-sm text-[#54595d]">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-[#a2a9b1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#d5fdd5] flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#14866d]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#202122]">{pages.length}</p>
                <p className="text-sm text-[#54595d]">Total Pages</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-[#a2a9b1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#fef6e7] flex items-center justify-center">
                <Flag className="h-5 w-5 text-[#ac6600]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#202122]">{reports.filter(r => r.status === 'pending').length}</p>
                <p className="text-sm text-[#54595d]">Pending Reports</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-[#a2a9b1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#eaf3ff] flex items-center justify-center">
                <span className="text-[#36c] font-bold">★</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#202122]">{users.filter(u => u.role === 'premium').length}</p>
                <p className="text-sm text-[#54595d]">Premium Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="bg-white rounded-lg border border-[#a2a9b1]">
          <TabsList className="border-b border-[#c8ccd1] w-full justify-start rounded-none p-0 bg-transparent">
            <TabsTrigger
              value="users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#36c] data-[state=active]:bg-transparent text-[#54595d] data-[state=active]:text-[#202122]"
              data-testid="users-tab"
            >
              Users
            </TabsTrigger>
            <TabsTrigger
              value="pages"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#36c] data-[state=active]:bg-transparent text-[#54595d] data-[state=active]:text-[#202122]"
              data-testid="pages-tab"
            >
              Pages
            </TabsTrigger>
            <TabsTrigger
              value="reports"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#36c] data-[state=active]:bg-transparent text-[#54595d] data-[state=active]:text-[#202122]"
              data-testid="reports-tab"
            >
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#72777d]" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10 bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                <SelectTrigger className="w-40 bg-white border-[#a2a9b1] text-[#202122]" data-testid="role-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#a2a9b1]">
                  <SelectItem value="all" className="text-[#202122]">All Roles</SelectItem>
                  <SelectItem value="regular" className="text-[#202122]">Regular</SelectItem>
                  <SelectItem value="premium" className="text-[#202122]">Premium</SelectItem>
                  <SelectItem value="admin" className="text-[#202122]">Admin</SelectItem>
                  <SelectItem value="super-admin" className="text-[#202122]">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-[#c8ccd1] rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#c8ccd1] bg-[#f8f9fa]">
                    <SortHeader label="Username" field="username" sort={userSort} onSort={(f) => toggleSort(userSort, f, setUserSort)} />
                    <SortHeader label="Email" field="email" sort={userSort} onSort={(f) => toggleSort(userSort, f, setUserSort)} />
                    <SortHeader label="Role" field="role" sort={userSort} onSort={(f) => toggleSort(userSort, f, setUserSort)} />
                    <SortHeader label="Created" field="created_at" sort={userSort} onSort={(f) => toggleSort(userSort, f, setUserSort)} />
                    {isSuperAdmin() && <TableHead className="text-[#54595d]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id} className="border-[#c8ccd1] hover:bg-[#f8f9fa]" data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium text-[#202122]">{u.username}</TableCell>
                      <TableCell className="text-[#54595d]">{u.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.role === 'super-admin' ? 'bg-[#f3e8ff] text-[#7c3aed]' :
                          u.role === 'admin' ? 'bg-[#fef6e7] text-[#ac6600]' :
                          u.role === 'premium' ? 'bg-[#eaf3ff] text-[#36c]' :
                          'bg-[#eaecf0] text-[#54595d]'
                        }`}>
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-[#54595d]">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      {isSuperAdmin() && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#d33] hover:bg-[#fee7e6] hover:text-[#d33] h-8 w-8 p-0"
                            onClick={() => setDeleteTarget({ id: u.id, username: u.username })}
                            disabled={u.id === user?.id}
                            title={u.id === user?.id ? "Cannot delete your own account" : "Delete user"}
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages" className="p-6">
            <div className="border border-[#c8ccd1] rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#c8ccd1] bg-[#f8f9fa]">
                    <SortHeader label="Title" field="title" sort={pageSort} onSort={(f) => toggleSort(pageSort, f, setPageSort)} />
                    <SortHeader label="Status" field="status" sort={pageSort} onSort={(f) => toggleSort(pageSort, f, setPageSort)} />
                    <SortHeader label="Views" field="view_count" sort={pageSort} onSort={(f) => toggleSort(pageSort, f, setPageSort)} />
                    <SortHeader label="Updated" field="updated_at" sort={pageSort} onSort={(f) => toggleSort(pageSort, f, setPageSort)} />
                    <TableHead className="text-[#54595d]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedPages.map((page) => (
                    <TableRow key={page.id} className="border-[#c8ccd1] hover:bg-[#f8f9fa]" data-testid={`page-row-${page.id}`}>
                      <TableCell className="font-medium">
                        <Link to={`/wiki/${page.slug}`} className="text-[#36c] hover:underline">
                          {page.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {page.is_sponsored && (
                            <span className="px-2 py-0.5 bg-[#eaf3ff] text-[#36c] text-xs rounded-full">Sponsored</span>
                          )}
                          {page.is_locked && (
                            <span className="px-2 py-0.5 bg-[#fee7e6] text-[#d33] text-xs rounded-full">Locked</span>
                          )}
                          {page.is_removed && (
                            <span className="px-2 py-0.5 bg-[#eaecf0] text-[#54595d] text-xs rounded-full">Removed</span>
                          )}
                          {!page.is_sponsored && !page.is_locked && !page.is_removed && (
                            <span className="text-[#72777d] text-xs">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[#54595d]">{page.view_count ?? 0}</TableCell>
                      <TableCell className="text-[#54595d]">{new Date(page.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Link to={`/wiki/${page.slug}/edit`}>
                          <Button variant="outline" size="sm" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0]">
                            Edit
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#54595d]">
                {showArchived ? 'Showing archived reports' : 'Showing active reports'}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-[#a2a9b1] text-[#54595d] hover:bg-[#eaecf0]"
                onClick={() => setShowArchived(v => !v)}
                data-testid="toggle-archived-reports"
              >
                <Archive className="h-4 w-4 mr-2" />
                {showArchived ? 'View Active' : 'View Archived'}
              </Button>
            </div>

            <div className="border border-[#c8ccd1] rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#c8ccd1] bg-[#f8f9fa]">
                    <SortHeader label="Issue" field="issue_type" sort={reportSort} onSort={(f) => toggleSort(reportSort, f, setReportSort)} />
                    <SortHeader label="Page" field="page_title" sort={reportSort} onSort={(f) => toggleSort(reportSort, f, setReportSort)} />
                    <SortHeader label="Status" field="status" sort={reportSort} onSort={(f) => toggleSort(reportSort, f, setReportSort)} />
                    <SortHeader label="Created" field="created_at" sort={reportSort} onSort={(f) => toggleSort(reportSort, f, setReportSort)} />
                    {isSuperAdmin() && <TableHead className="text-[#54595d]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedReports.length === 0 ? (
                    <TableRow className="border-[#c8ccd1]">
                      <TableCell colSpan={isSuperAdmin() ? 5 : 4} className="text-center text-[#54595d] py-8">
                        {showArchived ? 'No archived reports' : 'No active reports'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedReports.map((report) => (
                      <TableRow key={report.id} className="border-[#c8ccd1] hover:bg-[#f8f9fa]" data-testid={`report-row-${report.id}`}>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            report.issue_type === 'copyright' ? 'bg-[#eaf3ff] text-[#36c]' :
                            report.issue_type === 'privacy' ? 'bg-[#f3e8ff] text-[#7c3aed]' :
                            report.issue_type === 'defamation' ? 'bg-[#fee7e6] text-[#d33]' :
                            report.issue_type === 'harassment' ? 'bg-[#fef6e7] text-[#ac6600]' :
                            'bg-[#eaecf0] text-[#54595d]'
                          }`}>
                            {report.issue_type}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium text-[#202122]">{report.page_title || 'N/A'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            report.status === 'pending' ? 'bg-[#fef6e7] text-[#ac6600]' :
                            report.status === 'resolved' ? 'bg-[#d5fdd5] text-[#14866d]' :
                            report.status === 'archived' ? 'bg-[#eaecf0] text-[#54595d]' :
                            'bg-[#eaecf0] text-[#54595d]'
                          }`}>
                            {report.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-[#54595d]">{new Date(report.created_at).toLocaleDateString()}</TableCell>
                        {isSuperAdmin() && (
                          <TableCell>
                            {report.status !== 'archived' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#54595d] hover:bg-[#eaecf0] h-8 px-2 text-xs"
                                onClick={() => handleArchiveReport(report.id)}
                                data-testid={`archive-report-${report.id}`}
                              >
                                <Archive className="h-3.5 w-3.5 mr-1" />
                                Archive
                              </Button>
                            ) : (
                              <span className="text-xs text-[#72777d]">Archived</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete User Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-white border border-[#a2a9b1]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#202122]">Delete User</AlertDialogTitle>
              <AlertDialogDescription className="text-[#54595d]">
                Are you sure you want to permanently delete <strong>{deleteTarget?.username}</strong>?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#a2a9b1] text-[#202122] hover:bg-[#eaecf0]" data-testid="cancel-delete-user">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-[#d33] hover:bg-[#b32] text-white"
                onClick={handleDeleteUser}
                data-testid="confirm-delete-user"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
